import React, { useMemo } from "react";
import * as THREE from "three";
import { sortPointsClockwise } from "../point-sorting.component";
import ClipperLib from 'clipper-lib';


const Mask3D = ({ mask, maskList, overlapInfo, gridScaleX, gridScaleY, renderOrderBase = 400 }) => {
    const z = 0.1;

    const { shape, outline } = useMemo(() => {
        if (!mask || !mask.visible) return {};

        let points = [];

        if (mask.type === "polygon" && mask.points?.length >= 2) {
            points = mask.points.map((p) => new THREE.Vector2(p.x / gridScaleX, (p.y / gridScaleY) * -1));
        }

        if (mask.type === "rectangle") {
            const { x, y, width, height } = mask;
            const scaledX = x / gridScaleX;
            const scaledY = (y / gridScaleY) * -1;
            const scaledWidth = width / gridScaleX;
            const scaledHeight = height / gridScaleY;

            points = [
                new THREE.Vector2(scaledX, scaledY),
                new THREE.Vector2(scaledX + scaledWidth, scaledY),
                new THREE.Vector2(scaledX + scaledWidth, scaledY - scaledHeight),
                new THREE.Vector2(scaledX, scaledY - scaledHeight),
            ];
        }

        if (mask.type === "circle") {
            const { x, y, radius } = mask;
            const segments = 64;
            const angleStep = (Math.PI * 2) / segments;

            const centerX = x / gridScaleX;
            const centerY = (y / gridScaleY) * -1;
            const r = radius / gridScaleX;

            points = Array.from({ length: segments }, (_, i) => {
                const angle = i * angleStep;
                return new THREE.Vector2(
                    centerX + r * Math.cos(angle),
                    centerY + r * Math.sin(angle)
                );
            });
        }

        if (!points || points.length < 3) return {};

        if (mask.type === "rectangle" || mask.type === "circle") {
            const scaleX = mask.scaleX ?? 1;
            const scaleY = mask.scaleY ?? 1;
            const rotationDeg = mask.rotation ?? 0;
            const rotationRad = -(rotationDeg * Math.PI) / 180;

            const anchor = new THREE.Vector2(mask.x / gridScaleX, (mask.y / gridScaleY) * -1);

            points = points.map((p) => {
                const translated = new THREE.Vector2().subVectors(p, anchor);
                translated.x *= scaleX;
                translated.y *= scaleY;

                const rotated = new THREE.Vector2(
                    translated.x * Math.cos(rotationRad) - translated.y * Math.sin(rotationRad),
                    translated.x * Math.sin(rotationRad) + translated.y * Math.cos(rotationRad)
                );

                return rotated.add(anchor);
            });

        }

        const shape = new THREE.Shape(points);
        const outline = points.map((p) => [p.x, p.y, z]);

        const scale = 10000;
        const pathsToUnion = [];

        overlapInfo.forEach((overlap) => {
            if (overlap.mask1Id === mask.id || overlap.mask2Id === mask.id) {
                const originalPoints = sortPointsClockwise(overlap, maskList);
                if (originalPoints.length > 2) {
                    const clipperPath = originalPoints.map(p => ({
                        X: Math.round(p.x * scale) / gridScaleX,
                        Y: Math.round(p.y * scale) / gridScaleY
                    }));
                    pathsToUnion.push(clipperPath);
                }
            }
        });

        // Union all paths into one or more merged polygons
        const clipper = new ClipperLib.Clipper();
        clipper.AddPaths(pathsToUnion, ClipperLib.PolyType.ptSubject, true);

        const unionResult = [];
        clipper.Execute(ClipperLib.ClipType.ctUnion, unionResult, ClipperLib.PolyFillType.pftNonZero, ClipperLib.PolyFillType.pftNonZero);

        // Inset each resulting shape
        const co = new ClipperLib.ClipperOffset();
        unionResult.forEach(path => {
            co.AddPath(path, ClipperLib.JoinType.jtMiter, ClipperLib.EndType.etClosedPolygon);
        });

        const insetResult = [];
        co.Execute(insetResult, -0.08 * scale);

        insetResult.forEach(insetPath => {
            const holePoints = insetPath.map(p =>
                new THREE.Vector2((p.X / scale), (p.Y / scale) * -1)
            );
            holePoints.push(holePoints[0]); // close path

            const holePath = new THREE.Path(holePoints);
            shape.holes.push(holePath);
        });



        return { shape, outline };
    }, [mask, overlapInfo]);

    if (!shape || !outline) return null;


    const color = mask.color || "lightblue";
    const opacity = mask.opacity || 0.3;

    const zLift = -0.1 - (mask.layer || 0) * 0.01;

    return (
        <>
            <mesh position={[0, 0, zLift]} renderOrder={renderOrderBase + (mask.layer || 0)}>
                <shapeBufferGeometry args={[shape]} />
                <meshBasicMaterial
                    color={color}
                    opacity={opacity}
                    transparent
                    side={THREE.BackSide}
                    depthTest
                    depthWrite={false}
                    polygonOffset
                    polygonOffsetFactor={-10 * (mask.layer || 0)}
                    polygonOffsetUnits={-10}
                />
            </mesh>
        </>
    );
};

export default Mask3D;
